/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { IconCalendar } from '@hulu-react-style-components/icons';
import envConfig from '@hulu/env-config';
import * as React from 'react';
import type { Cell } from 'react-table';

import DateHeaderLabel from '../../../../common/DateHeaderLabel/DateHeaderLabel';
import { getTimezoneWithGMT } from '../../../../common/SelectTimeZone/utils';
import { resizingData, stickyBlock } from '../../../../common/TraffickingTable/utils';
import { UNKNOWN_TIMEZONE } from '../../../../configs';
import { TraffickingTableName } from '../../../../constants';
import bem from '../../../../utils/bem';
import { fromEmailToName } from '../../../../utils/common';
import { formatIndustryList } from '../../../../utils/formatting';
import { DEFAULT_COLUMN_MIN_WIDTH } from '../../constants';
import { CampaignColumn, CampaignColumnMeta } from '../../constants/campaign';
import { getAlertMessage } from '../../ExportDropdown/utils/commonAccessors';
import { AlertCell, CampaignLeadCell, MicroStrategyReportCell } from '../cells';
import type { AlertFlagProps } from '../cells/AlertCell';
import { AlertFiled } from '../cells/AlertCell/constants';
import CampaignStatusCell from '../cells/CampaignStatusCell';
import CountryCell from '../cells/CountryCell';
import { getUniqueCountriesString } from '../cells/CountryCell/utils';
import IndustryCell from '../cells/IndustryCell/IndustryCell';
import NameCell from '../cells/NameCell';
import OmsCell from '../cells/OmsAlertCell/OmsAlert';
import { EntityType } from '../hooks/makePageDrawerPlugin';
import type { CampaignModel } from '../modelConverters';
import type { CustomColumnConfig } from './utils';
import { formatMicroCurrencyAmount, formatStartEndDateString, stringToDateTimeString } from './utils';

const [, element] = bem('alert-cell');

export const getCampaignAlertFlags = ({
  id,
  hasZeroDeliveryRisk,
  hasMisalignedSchedule,
}: CampaignModel): AlertFlagProps[] => {
  return [
    {
      id,
      field: AlertFiled.ZeroDelivery,
      isShown: !!hasZeroDeliveryRisk,
      hoverMessage:
        'This campaign contains 1+ Line Item(s) that have not delivered any impressions in the last 12 hours.',
    },
    {
      id,
      field: AlertFiled.MisalignedSchedule,
      isShown: !!hasMisalignedSchedule,
      icon: <IconCalendar width={18} height={18} />,
      hoverMessage: 'This campaign contains 1+ Line Item(s) with schedule conflict alert.',
      className: element('red-icon'),
    },
  ];
};

const getMSTRReportLinkColumn = (): CustomColumnConfig<CampaignModel> => {
  return {
    id: CampaignColumn.MicroStrategyReport,
    Header: CampaignColumnMeta[CampaignColumn.MicroStrategyReport].header,
    Cell: MicroStrategyReportCell,
    width: resizingData.getData(85, TraffickingTableName.campaigns, 'MicroStrategyReport'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    className: 'mstr-cell',
  };
};

type GetCampaignColumnsProps = {
  hasAccessToChangeLog?: boolean;
  unacknowledgedCampaignIds?: string[];
  disabledColumns?: typeof CampaignColumn[keyof typeof CampaignColumn][];
};

const getCampaignsColumns = ({
  hasAccessToChangeLog,
  unacknowledgedCampaignIds,
  disabledColumns,
}: GetCampaignColumnsProps = {}): CustomColumnConfig<CampaignModel>[] => [
  {
    id: CampaignColumn.Alert,
    Header: CampaignColumnMeta[CampaignColumn.Alert].header,
    Cell: (cell: Cell<CampaignModel>) => <AlertCell flags={getCampaignAlertFlags(cell.row.original)} />,
    width: resizingData.getData(90, TraffickingTableName.campaigns, 'Alert'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.Alert].tooltip,
    exportAccessor: (c) =>
      getAlertMessage({
        hasZeroDeliveryRisk: !!c.hasZeroDeliveryRisk,
        hasMisalignedSchedule: !!c.hasMisalignedSchedule,
      }),
  },
  ...(disabledColumns?.includes(CampaignColumn.Oms)
    ? []
    : [
        {
          id: CampaignColumn.Oms,
          Header: CampaignColumnMeta[CampaignColumn.Oms].header,
          Cell: (cell: Cell<CampaignModel>) => (
            <OmsCell
              id={cell.row.original.id}
              campaignName={cell.row.original.name}
              unacknowledgedCampaignIds={unacknowledgedCampaignIds}
              isOrderTypeSelfService={cell.row.original.orderType?.displayName === 'Self Service'}
            />
          ),
          width: resizingData.getData(115, TraffickingTableName.campaigns, 'OMS'),
        },
      ]),
  {
    id: CampaignColumn.Name,
    accessor: 'name',
    Header: CampaignColumnMeta[CampaignColumn.Name].header,
    width: resizingData.getData(350, TraffickingTableName.campaigns, 'Name'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    Cell: ({ row: { original }, openTraffickingPageDrawer }) => (
      <NameCell
        rowOriginal={original}
        entityType={EntityType.CAMPAIGN}
        openTraffickingPageDrawer={hasAccessToChangeLog ? openTraffickingPageDrawer : () => {}}
      />
    ),
    className: stickyBlock('name'),
    disableHiding: true,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.Name].tooltip,
  },
  ...(envConfig.REACT_APP_FEATURE_ENABLE_MSTR_REPORT_LINK === 'true' ? [getMSTRReportLinkColumn()] : []),
  {
    id: CampaignColumn.TrackingId,
    accessor: 'trackingId',
    Header: CampaignColumnMeta[CampaignColumn.TrackingId].header,
    width: resizingData.getData(175, TraffickingTableName.campaigns, 'TrackingId'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.TrackingId].tooltip,
    exportAccessor: (c) => c.trackingId ?? '',
  },
  {
    id: CampaignColumn.Brand,
    accessor: (c) => {
      return c.crmLink?.name;
    },
    Header: CampaignColumnMeta[CampaignColumn.Brand].header,
    width: resizingData.getData(350, TraffickingTableName.campaigns, 'brand'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.Brand].tooltip,
    exportAccessor: (c) => c.crmLink?.name ?? '',
  },
  {
    id: CampaignColumn.Country,
    accessor: ({ campaignLineItems }) => campaignLineItems || [],
    Header: CampaignColumnMeta[CampaignColumn.Country].header,
    width: resizingData.getData(115, TraffickingTableName.lineItems, 'Country'),
    Cell: (cell: Cell<CampaignModel>) => <CountryCell lineItemList={cell.value} />,
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.Country].tooltip,
    exportAccessor: (c) => getUniqueCountriesString(c.campaignLineItems),
  },
  {
    id: CampaignColumn.OrderType,
    accessor: (c) => c.orderType?.displayName,
    Header: CampaignColumnMeta[CampaignColumn.OrderType].header,
    width: resizingData.getData(170, TraffickingTableName.campaigns, 'OrderType'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.OrderType].tooltip,
    exportAccessor: (c) => c.orderType?.displayName ?? '',
  },
  {
    id: CampaignColumn.BillingCalendar,
    accessor: (c) => c.billingCalendar?.displayName,
    Header: CampaignColumnMeta[CampaignColumn.BillingCalendar].header,
    width: resizingData.getData(170, TraffickingTableName.campaigns, 'BillingCalendar'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.BillingCalendar].tooltip,
    exportAccessor: (c) => c.billingCalendar?.displayName ?? '',
  },
  {
    id: CampaignColumn.OrderOmsLinkId,
    accessor: (c) => c.orderOmsLink?.id,
    Header: CampaignColumnMeta[CampaignColumn.OrderOmsLinkId].header,
    width: resizingData.getData(250, TraffickingTableName.campaigns, 'OrderOmsLinkId'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.OrderOmsLinkId].tooltip,
    exportAccessor: (c) => c.orderOmsLink?.id ?? '',
  },
  {
    id: CampaignColumn.TraffickerEmail,
    accessor: 'traffickerEmail',
    Header: CampaignColumnMeta[CampaignColumn.TraffickerEmail].header,
    Cell: CampaignLeadCell,
    isEditable: true,
    width: resizingData.getData(210, TraffickingTableName.campaigns, 'TraffickerEmail'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.TraffickerEmail].tooltip,
    exportAccessor: (c) => (c.traffickerEmail ? fromEmailToName(c.traffickerEmail) : ''),
  },
  {
    id: CampaignColumn.OrderSalespersonEmail,
    accessor: (campaign) => (campaign.salespersonEmail ? fromEmailToName(campaign.salespersonEmail) : ''),
    Header: CampaignColumnMeta[CampaignColumn.OrderSalespersonEmail].header,
    width: resizingData.getData(210, TraffickingTableName.campaigns, 'OrderSalespersonEmail'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.OrderSalespersonEmail].tooltip,
    exportAccessor: (c) => (c.salespersonEmail ? fromEmailToName(c.salespersonEmail) : ''),
  },
  {
    id: CampaignColumn.Start,
    accessor: (c) => c.startDate,
    Header: (
      <DateHeaderLabel
        label={CampaignColumnMeta[CampaignColumn.Start].header}
        subLabel={CampaignColumnMeta[CampaignColumn.Start].subHeader}
        dataTestid="start-date-header"
      />
    ),
    headerString: CampaignColumnMeta[CampaignColumn.Start].header,
    Cell: formatStartEndDateString,
    width: resizingData.getData(210, TraffickingTableName.campaigns, 'Start'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.Start].tooltip,
    exportAccessor: (c) => formatStartEndDateString({ value: c.startDate }) ?? '',
  },
  {
    id: CampaignColumn.End,
    accessor: (c) => c.endDate,
    Header: (
      <DateHeaderLabel
        label={CampaignColumnMeta[CampaignColumn.End].header}
        subLabel={CampaignColumnMeta[CampaignColumn.End].subHeader}
        dataTestid="end-date-header"
      />
    ),
    headerString: CampaignColumnMeta[CampaignColumn.End].header,
    Cell: formatStartEndDateString,
    width: resizingData.getData(210, TraffickingTableName.campaigns, 'End'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.End].tooltip,
    exportAccessor: (c) => formatStartEndDateString({ value: c.endDate }) ?? '',
  },
  {
    id: CampaignColumn.Timezone,
    accessor: (c) => (c.timezone ? getTimezoneWithGMT(c.timezone) : UNKNOWN_TIMEZONE),
    Header: CampaignColumnMeta[CampaignColumn.Timezone].header,
    width: resizingData.getData(230, TraffickingTableName.campaigns, 'Timezone'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.Timezone].tooltip,
    exportAccessor: (c) => (c.timezone ? getTimezoneWithGMT(c.timezone) : UNKNOWN_TIMEZONE),
  },
  {
    id: CampaignColumn.Advertiser,
    accessor: (c) => c.advertiser,
    Header: CampaignColumnMeta[CampaignColumn.Advertiser].header,
    width: resizingData.getData(330, TraffickingTableName.campaigns, 'advertiser'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.Advertiser].tooltip,
    exportAccessor: (c) => c.advertiser ?? '',
  },
  {
    id: CampaignColumn.IndustryName,
    accessor: (c) => {
      if (Array.isArray(c.industryList)) {
        return c.industryList.map((industry) => industry.name).join('; ');
      }
    },
    Header: CampaignColumnMeta[CampaignColumn.IndustryName].header,
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    Cell: IndustryCell,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.IndustryName].tooltip,
    exportAccessor: (c) => formatIndustryList(c.industryList ?? []),
  },
  {
    id: CampaignColumn.Agency,
    accessor: (c) => c.agency,
    Header: CampaignColumnMeta[CampaignColumn.Agency].header,
    width: resizingData.getData(330, TraffickingTableName.campaigns, 'agency'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.Agency].tooltip,
    exportAccessor: (c) => c.agency ?? '',
  },
  {
    id: CampaignColumn.CampaignID,
    accessor: 'id',
    Header: CampaignColumnMeta[CampaignColumn.CampaignID].header,
    width: resizingData.getData(320, TraffickingTableName.campaigns, 'campaignID'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.CampaignID].tooltip,
  },
  {
    id: CampaignColumn.CreatedAt,
    accessor: 'createdAt',
    Header: CampaignColumnMeta[CampaignColumn.CreatedAt].header,
    Cell: stringToDateTimeString,
    width: resizingData.getData(190, TraffickingTableName.campaigns, 'CreatedAt'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.CreatedAt].tooltip,
    exportAccessor: (c) => stringToDateTimeString({ value: c.createdAt }) ?? '',
  },
  {
    id: CampaignColumn.UpdatedAt,
    accessor: 'updatedAt',
    Header: CampaignColumnMeta[CampaignColumn.UpdatedAt].header,
    Cell: stringToDateTimeString,
    width: resizingData.getData(185, TraffickingTableName.campaigns, 'UpdatedAt'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.UpdatedAt].tooltip,
    exportAccessor: (c) => stringToDateTimeString({ value: c.updatedAt }) ?? '',
  },
  {
    id: CampaignColumn.AdAccount,
    accessor: (c) => c.adAccount?.name,
    Header: CampaignColumnMeta[CampaignColumn.AdAccount].header,
    width: resizingData.getData(320, TraffickingTableName.campaigns, 'adAccount'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.AdAccount].tooltip,
    exportAccessor: (c) => c.adAccount?.name ?? '',
  },
  {
    id: CampaignColumn.AdAccountId,
    accessor: (c) => c.adAccount?.id,
    Header: CampaignColumnMeta[CampaignColumn.AdAccountId].header,
    width: resizingData.getData(324, TraffickingTableName.campaigns, 'AdAccountId'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.AdAccountId].tooltip,
    exportAccessor: (c) => c.adAccount?.id ?? '',
  },
  {
    id: CampaignColumn.BrandId,
    accessor: (c) => c.crmLink?.id,
    Header: CampaignColumnMeta[CampaignColumn.BrandId].header,
    width: resizingData.getData(200, TraffickingTableName.campaigns, 'BrandId'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.BrandId].tooltip,
    exportAccessor: (c) => c.crmLink?.id ?? '',
  },
  {
    id: CampaignColumn.CampaignBudget,
    accessor: 'budget',
    Header: CampaignColumnMeta[CampaignColumn.CampaignBudget].header,
    Cell: ({ value, row }: Cell<CampaignModel>) => (
      <span>{formatMicroCurrencyAmount(value, row.original.campaignLineItems?.[0]?.unitCostDto?.currency)}</span>
    ),
    width: resizingData.getData(180, TraffickingTableName.campaigns, 'campaignBudget'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    className: 'price-cell',
    tooltipMessage: CampaignColumnMeta[CampaignColumn.CampaignBudget].tooltip,
    exportAccessor: (c) => formatMicroCurrencyAmount(c.budget, c.campaignLineItems?.[0]?.unitCostDto?.currency) ?? '',
  },
  {
    id: CampaignColumn.Status,
    accessor: (c) => c?.status,
    Cell: CampaignStatusCell,
    Header: CampaignColumnMeta[CampaignColumn.Status].header,
    width: resizingData.getData(180, TraffickingTableName.campaigns, 'Status'),
    minWidth: DEFAULT_COLUMN_MIN_WIDTH,
    tooltipMessage: CampaignColumnMeta[CampaignColumn.Status].tooltip,
    exportAccessor: (c) => c.status?.displayName ?? '',
  },
];

export default getCampaignsColumns;
